import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, resolveDirective as _resolveDirective, withDirectives as _withDirectives, toDisplayString as _toDisplayString, withCtx as _withCtx, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "page-content" }
const _hoisted_2 = { class: "flex grid p-3 justify-content-between" }
const _hoisted_3 = { class: "flex flex-row gap-1 align-items-center justify-content-start font-bold text-sm" }
const _hoisted_4 = { class: "justify-content-end flex flex-row" }
const _hoisted_5 = { class: "col-fixed" }
const _hoisted_6 = { class: "col-fixed" }

import { computed, onMounted, ref } from 'vue';
import Calendar from 'primevue/calendar';
import Column from 'primevue/column';
import DataTable, { DataTablePageEvent } from 'primevue/datatable';
import { can, useDateTime, useRouting } from '@/composables';
import { usePayrollReportStore, useReportStore } from '@/store';
import { DateFilterInterface, OrganizationDocumentsFilterInterface } from '@/types';
import { PERMISSIONS } from '@/enum';


export default /*@__PURE__*/_defineComponent({
  __name: 'PayrollReport',
  setup(__props) {

const startDate = ref();
const endDate = ref();
const defaultFilters: OrganizationDocumentsFilterInterface = {
  page: 1,
  start_at: '',
  end_at: '',
  per_page: 10,
};

const $routing = useRouting();
const payrollReportStore = usePayrollReportStore();
const reportStore = useReportStore();
const filters = ref<OrganizationDocumentsFilterInterface>(defaultFilters);
const baseDates = ref(useDateTime().baseDates());

onMounted(() => init());

const isPayrollReport = computed<boolean>(() => $routing.route.name === 'reports.payroll');

const init = async () => {
  if (isPayrollReport.value) {
    await setDates();
    await defineFilters();
    await payrollReportStore.getPayroll(payload.value);
  }
};

$routing.routeChangeWatcher(async () => {
  if (isPayrollReport.value) {
    if (!datesInverted.value) await payrollReportStore.getPayroll(payload.value);
    await defineFilters();
    await reportStore.getReportServiceProviderFilter(filters.value.start_at, filters.value.end_at);
  }
});

const onStartDateRangeChange = async (dateStart: Date) => {
  if (!datesInverted.value) {
    const start_at = useDateTime().formatDateForApi(dateStart);
    const end = new Date(endDate.value.getFullYear(), endDate.value.getMonth() + 1, 0);
    const end_at = useDateTime().formatDateForApi(end);

    await reportStore.getReportServiceProviderFilter(start_at, end_at);

    $routing.addQueryParams({ start_at, end_at });
  }
};

const onEndDateRangeChange = async (dateEnd: Date) => {
  const end = new Date(dateEnd.getFullYear(), dateEnd.getMonth() + 1, 0);
  endDate.value = end;
  if (!datesInverted.value) {
    const start_at = useDateTime().formatDateForApi(startDate.value);
    const end_at = useDateTime().formatDateForApi(end);
    $routing.addQueryParams({ start_at, end_at });
  }
};

/**
 * set up the dates
 */
const setDates = async () => {
  startDate.value = baseDates.value[0];
  endDate.value = baseDates.value[1];
  const query = $routing.route.query;

  //if there is no query_params for start and end dates add them
  if (!query.end_at && !query.start_at) {
    $routing.addQueryParams({
      start_at: useDateTime().formatDateForApi(startDate.value),
      end_at: useDateTime().formatDateForApi(endDate.value),
      per_page: defaultFilters.per_page,
      page: defaultFilters.page,
    });
  }
};

/**
 * add payload instead of route params for calls to the API
 * TODO: add organization array and selected clinicians
 */
const payload = computed<DateFilterInterface>(() => {
  const end = new Date(endDate.value.getFullYear(), endDate.value.getMonth() + 1, 0);
  return {
    start_at: useDateTime().formatDateForApi(startDate.value),
    end_at: useDateTime().formatDateForApi(end),
    per_page: filters.value.per_page,
    page: filters.value.page,
  };
});

/**
 * Define the filters based on the query params available on page load.
 */
const defineFilters = async () => {
  const query = $routing.route.query;

  filters.value = {
    start_at: query.start_at ? (query.start_at as string) : '',
    end_at: query.end_at ? (query.end_at as string) : '',
    page: query.page ? parseInt(query.page as string) : 1,
    per_page: query.per_page ? parseInt(query.per_page as string) : defaultFilters.per_page,
  };
};

/**
 * Check if the user is allowed to download the report based on the resultset totals and the limit.
 */
const canDownload = computed<boolean>(
  () =>
    payrollReportStore.payroll.total < payrollReportStore.payroll.limit && can([PERMISSIONS.REPORTS_PAYMENT_DOWNLOAD]),
);

/**
 * Handle the pagination of the request data.
 */
const onPage = (event: DataTablePageEvent) => {
  filters.value.page = event.page + 1;
  filters.value.per_page = event.rows;
  $routing.addQueryParams({ page: event.page + 1, per_page: event.rows });
};

/**
 * Handle the export action for the specific report type.
 */
const onExportReport = (type: string) => {
  if (canDownload.value) {
    payrollReportStore.downloadPayrollReport($routing.route.query, type);
  } else {
    payrollReportStore.doToast(
      'You will receive an email with a link to download the exported data.',
      'Your export has started',
    );
  }
};

/**
 * Define the tooltip text for the export buttons.
 */
const tooltipExportText = (type: string) => (canDownload.value ? `Download as ${type}` : `Email as ${type}`);

const datesInverted = computed(() => {
  return startDate.value && endDate.value ? startDate.value.valueOf() >= endDate.value.valueOf() : false;
});

return (_ctx: any,_cache: any) => {
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_Card = _resolveComponent("Card")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_Card, null, {
      content: _withCtx(() => [
        _createVNode(_unref(DataTable), {
          filters: filters.value,
          "onUpdate:filters": _cache[4] || (_cache[4] = ($event: any) => ((filters).value = $event)),
          "filter-display": "menu",
          value: _unref(payrollReportStore).payroll.data,
          paginator: true,
          rows: filters.value.per_page,
          "total-records": _unref(payrollReportStore).payroll.total,
          "data-key": "id",
          "row-hover": true,
          lazy: true,
          "loading-icon": "logo-spinner",
          loading: _unref(payrollReportStore).payroll.loading,
          "rows-per-page-options": [10, 20, 50, 100],
          "paginator-template": "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown",
          "current-page-report-template": "Showing {first} to {last} of {totalRecords}",
          "responsive-layout": "scroll",
          onPage: onPage
        }, {
          header: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createVNode(_unref(Calendar), {
                  modelValue: startDate.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((startDate).value = $event)),
                  view: "month",
                  class: "w-full text-sm",
                  "date-format": "MM yy",
                  onDateSelect: onStartDateRangeChange
                }, null, 8, ["modelValue"]),
                _cache[5] || (_cache[5] = _createTextVNode(" to ", -1)),
                _createVNode(_unref(Calendar), {
                  modelValue: endDate.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((endDate).value = $event)),
                  view: "month",
                  class: "w-full text-sm",
                  "date-format": "MM yy",
                  onDateSelect: onEndDateRangeChange
                }, null, 8, ["modelValue"])
              ]),
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, [
                  _withDirectives(_createVNode(_component_AppButton, {
                    type: "secondary",
                    text: "CSV",
                    "icon-left": "system_update_alt",
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (onExportReport('csv')))
                  }, null, 512), [
                    [
                      _directive_tooltip,
                      tooltipExportText('CSV'),
                      void 0,
                      { bottom: true }
                    ]
                  ])
                ]),
                _createElementVNode("div", _hoisted_6, [
                  _withDirectives(_createVNode(_component_AppButton, {
                    type: "secondary",
                    text: "XLSX",
                    "icon-left": "system_update_alt",
                    onClick: _cache[3] || (_cache[3] = ($event: any) => (onExportReport('xlsx')))
                  }, null, 512), [
                    [
                      _directive_tooltip,
                      tooltipExportText('XLSX'),
                      void 0,
                      { bottom: true }
                    ]
                  ])
                ])
              ])
            ])
          ]),
          empty: _withCtx(() => [...(_cache[6] || (_cache[6] = [
            _createTextVNode("No Payroll Found", -1)
          ]))]),
          loading: _withCtx(() => [...(_cache[7] || (_cache[7] = [
            _createTextVNode("Loading data. Please wait.", -1)
          ]))]),
          default: _withCtx(() => [
            (_unref(payrollReportStore).showAllColumns)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createVNode(_unref(Column), {
                    field: "external_id",
                    header: "ID",
                    "body-class": "text-center"
                  }),
                  _createVNode(_unref(Column), {
                    field: "bucket_start_at",
                    header: "Start Date",
                    "body-class": "text-center",
                    "header-class": "text-center"
                  }, {
                    body: _withCtx(({ data }) => [
                      _createTextVNode(_toDisplayString(_unref(useDateTime)().formatIntlDate(data.bucket_start_at)), 1)
                    ]),
                    _: 1
                  }),
                  _createVNode(_unref(Column), {
                    field: "bucket_end_at",
                    header: "End Date",
                    "body-class": "text-center",
                    "header-class": "text-center"
                  }, {
                    body: _withCtx(({ data }) => [
                      _createTextVNode(_toDisplayString(_unref(useDateTime)().formatIntlDate(data.bucket_end_at)), 1)
                    ]),
                    _: 1
                  }),
                  _createVNode(_unref(Column), {
                    field: "last_name",
                    header: "Last Name",
                    "body-class": "text-center",
                    "header-class": "text-center"
                  }),
                  _createVNode(_unref(Column), {
                    field: "first_name",
                    header: "First Name",
                    "body-class": "text-center",
                    "header-class": "text-center"
                  }),
                  _createVNode(_unref(Column), {
                    field: "organization_name",
                    header: "Organizations",
                    "body-class": "text-center"
                  }),
                  _createVNode(_unref(Column), {
                    field: "payment_type",
                    header: "Compensation Type",
                    "body-class": "text-center",
                    "header-class": "text-center"
                  }),
                  _createVNode(_unref(Column), {
                    field: "rate",
                    header: "Rate",
                    "body-class": "text-center"
                  }, {
                    body: _withCtx(({ data }) => [
                      _createTextVNode("$" + _toDisplayString(data.rate), 1)
                    ]),
                    _: 1
                  }),
                  _createVNode(_unref(Column), {
                    field: "hours",
                    header: "Hours",
                    "body-class": "text-center"
                  }),
                  _createVNode(_unref(Column), {
                    field: "total",
                    header: "Total",
                    "body-class": "text-center"
                  }, {
                    body: _withCtx(({ data }) => [
                      _createTextVNode("$" + _toDisplayString(data.total), 1)
                    ]),
                    _: 1
                  })
                ], 64))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["filters", "value", "rows", "total-records", "loading"])
      ]),
      _: 1
    })
  ]))
}
}

})